import { useState, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

// Set up the worker - use jsdelivr CDN which is more reliable
try {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;
} catch (err) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;
}

interface PDFViewerProps {
  url: string;
  fileName?: string;
  /** @deprecated No longer used – acknowledgement is handled by the parent */
  onAcknowledge?: () => void;
}

export default function PDFViewer({ url, fileName = "document.pdf" }: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const loadPDF = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const pdf = await pdfjsLib.getDocument(url).promise;
        setNumPages(pdf.numPages);
      } catch (err) {
        console.warn("PDF.js loading error (will use iframe fallback):", err);
        setError(null);
      } finally {
        setIsLoading(false);
      }
    };
    if (url) loadPDF();
  }, [url]);

  const handlePreviousPage = () => setCurrentPage((prev) => Math.max(1, prev - 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(numPages, prev + 1));
  const handleZoomIn = () => setScale((prev) => Math.min(2, prev + 0.1));
  const handleZoomOut = () => setScale((prev) => Math.max(0.5, prev - 0.1));
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 bg-red-50 rounded-lg border border-red-200">
        <div className="text-center">
          <p className="text-red-600 font-medium">Error loading PDF</p>
          <p className="text-sm text-red-500 mt-2">The PDF viewer encountered an issue. Please try downloading the file directly.</p>
          <Button onClick={handleDownload} className="mt-4" variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-0 ${isFullscreen ? "fixed inset-0 z-50 bg-black" : "h-full"}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-muted/60 border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || isLoading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-20 text-center tabular-nums">
            {isLoading ? "Loading..." : `${currentPage} / ${numPages}`}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage === numPages || isLoading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="sm" onClick={handleZoomOut} disabled={scale <= 0.5 || isLoading}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-12 text-center tabular-nums">
            {Math.round(scale * 100)}%
          </span>
          <Button variant="ghost" size="sm" onClick={handleZoomIn} disabled={scale >= 2 || isLoading}>
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsFullscreen(!isFullscreen)}
            disabled={isLoading}
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDownload} disabled={isLoading}>
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* PDF Container */}
      <div
        className={`overflow-auto bg-muted/20 flex items-start justify-center ${
          isFullscreen ? "h-[calc(100vh-48px)]" : "flex-1"
        }`}
        style={isFullscreen ? {} : { minHeight: 0 }}
      >
        {isLoading ? (
          <div className="flex flex-col items-center gap-2 mt-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <p className="text-sm text-muted-foreground">Loading PDF...</p>
          </div>
        ) : (
          <iframe
            src={`${url}#page=${currentPage}&zoom=${Math.round(scale * 100)}`}
            className="w-full h-full"
            title={fileName}
          />
        )}
      </div>
    </div>
  );
}
